import { useCallback, useEffect, useMemo, useState } from "react";
import { ReactFlow, Background, Controls, MiniMap, useNodesState, MarkerType } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { toast } from "sonner";
import { AlertOctagon, GitBranch, LayoutGrid, Save, Undo2, Route, Siren } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useData } from "../lib/DataContext";
import { PageHeader } from "../components/shared";
import FlowNode from "../components/FlowNode";
import BomEditor from "../components/BomEditor";
import BomDetailCard from "../components/BomDetailCard";
import { edgeId, tierError, findCycle, cycleEdgeIds, autoLayout, upstreamTotals } from "../lib/graph";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const nodeTypes = { flow: FlowNode };
const sig = (es) => es.map((e) => `${e.id}:${e.qty}`).sort().join("|");

function QtyDialog({ edit, byId, onSave, onDelete, onClose }) {
  const [qty, setQty] = useState("1");
  useEffect(() => { if (edit) setQty(String(edit.qty ?? 1)); }, [edit]);
  if (!edit) return null;
  const src = byId[edit.source], tgt = byId[edit.target];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F1623] border-[#334155] max-w-md" data-testid="graph-qty-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">{edit.isNew ? "Add component" : "Edit connection"}</DialogTitle>
          <DialogDescription className="text-[#9CA3AF]">{src?.name} → {tgt?.name}</DialogDescription>
        </DialogHeader>
        <label className="flex flex-col gap-1"><span className="font-mono text-[10px] tracking-[0.2em] text-[#6B7280]">QTY OF {src?.sku} PER 1 {tgt?.unit || "UNIT"} OF {tgt?.sku}</span>
          <input autoFocus type="number" step="any" min="0" data-testid="graph-qty-input" value={qty} onChange={(e) => setQty(e.target.value)} className="no-spin h-12 px-3 font-mono bg-[#090D16] border border-[#334155] rounded-md" /></label>
        <div className="flex gap-2">
          <button data-testid="graph-qty-save" disabled={!(Number(qty) > 0)} onClick={() => onSave(Number(qty))} className="flex-1 h-12 rounded-md bg-[#F59E0B] text-[#090D16] font-bold disabled:opacity-40">{edit.isNew ? "Add" : "Update"}</button>
          {!edit.isNew && <button data-testid="graph-edge-delete" onClick={onDelete} className="h-12 px-4 rounded-md border border-[#EF4444]/50 text-[#EF4444] font-semibold">Remove</button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LoopAlert({ chain, byId }) {
  if (!chain) return null;
  return (
    <div data-testid="graph-recursion-warning" className="absolute top-4 left-1/2 -translate-x-1/2 z-10 max-w-xl w-[92%] p-4 rounded-md border-2 border-[#EF4444] bg-[#1A0B0E]/95 backdrop-blur shadow-[0_0_30px_rgba(239,68,68,0.35)]">
      <div className="flex items-center gap-2 font-display font-extrabold text-[#EF4444]"><AlertOctagon className="w-5 h-5" />RECURSION WARNING</div>
      <div className="font-mono text-sm text-white mt-1" data-testid="graph-loop-chain">Loop Detected: {chain.map((i) => byId[i]?.sku || "?").join(" → ")}</div>
      <div className="text-xs text-[#FCA5A5] mt-1">A part can't be made from itself, directly or through other parts. Remove one of the red connections. Commit is blocked until the loop is gone.</div>
    </div>
  );
}

const Toggle = ({ on, onClick, icon: I, label, testId }) => (
  <button data-testid={testId} onClick={onClick} className={`h-12 px-4 rounded-md border text-sm font-semibold flex items-center gap-2 ${on ? "bg-white text-[#090D16] border-white" : "border-[#334155] text-[#9CA3AF]"}`}><I className="w-4 h-4" />{label}</button>
);

export default function BomGraph() {
  const { refreshAll } = useData();
  const [g, setG] = useState(null);
  const [draft, setDraft] = useState([]);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edit, setEdit] = useState(null);
  const [pathRoot, setPathRoot] = useState(null);
  const [pathMode, setPathMode] = useState(false);
  const [overlay, setOverlay] = useState(true);
  const [busy, setBusy] = useState(false);
  const [createFor, setCreateFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);

  const load = useCallback(() => api.get("/bom-graph").then((r) => { setG(r.data); setDraft(r.data.edges); }), []);
  useEffect(() => { load(); }, [load]);
  const byId = useMemo(() => Object.fromEntries((g?.nodes || []).map((n) => [n.id, n])), [g]);
  const chain = useMemo(() => findCycle(draft), [draft]);
  const badEdges = useMemo(() => cycleEdgeIds(chain), [chain]);
  const loopNodes = useMemo(() => new Set(chain || []), [chain]);
  const totals = useMemo(() => (pathRoot ? upstreamTotals(pathRoot, draft) : null), [pathRoot, draft]);
  const dirty = g && sig(draft) !== sig(g.edges);
  const layout = useMemo(() => (g ? autoLayout(g.nodes, draft) : { pos: {}, stages: {} }), [g, draft]);

  useEffect(() => {
    if (!g) return;
    setNodes((prev) => {
      const old = Object.fromEntries(prev.map((n) => [n.id, n.position]));
      return g.nodes.map((n) => ({
        id: n.id, type: "flow", position: old[n.id] || n.position || layout.pos[n.id],
        data: { node: n, stage: layout.stages[n.id], overlay, onCreateBom: dirty ? null : setCreateFor, onOpenBom: setDetailFor, inCycle: loopNodes.has(n.id), highlight: totals && totals[n.id] != null,
                dim: totals ? totals[n.id] == null : false, total: totals && n.id !== pathRoot ? totals[n.id] : null },
      }));
    });
  }, [g, layout, overlay, loopNodes, totals, pathRoot, setNodes, dirty]);

  const rfEdges = useMemo(() => draft.map((e) => {
    const bad = badEdges.has(e.id);
    const onPath = totals && totals[e.source] != null && totals[e.target] != null;
    const color = bad ? "#EF4444" : onPath ? "#F59E0B" : "#475569";
    return { id: e.id, source: e.source, target: e.target, label: `×${e.qty}`, animated: bad || onPath,
             style: { stroke: color, strokeWidth: bad ? 3.5 : onPath ? 2.5 : 1.5, opacity: totals && !onPath && !bad ? 0.15 : 1 },
             labelStyle: { fill: bad ? "#EF4444" : "#E5E7EB", fontFamily: "monospace", fontSize: 11 }, labelBgStyle: { fill: "#0F1623" },
             markerEnd: { type: MarkerType.ArrowClosed, color } };
  }), [draft, badEdges, totals]);

  const onConnect = (c) => {
    const err = tierError(byId[c.target], byId[c.source]);
    if (err) return toast.error(err);
    if (draft.some((e) => e.id === edgeId(c.source, c.target))) return toast.error("Already connected. Click the arrow to change its quantity");
    setEdit({ source: c.source, target: c.target, qty: 1, isNew: true });
  };
  const saveEdit = (qty) => {
    const id = edgeId(edit.source, edit.target);
    const next = edit.isNew ? [...draft, { id, source: edit.source, target: edit.target, qty }] : draft.map((e) => (e.id === id ? { ...e, qty } : e));
    setDraft(next); setEdit(null);
    const cyc = findCycle(next);
    if (cyc) toast.error(`Loop Detected: ${cyc.map((i) => byId[i]?.sku).join(" → ")}`);
  };
  const removeEdit = () => { setDraft(draft.filter((e) => e.id !== edgeId(edit.source, edit.target))); setEdit(null); };
  const commit = async () => {
    setBusy(true);
    try { const r = await api.post("/bom-graph/commit", { edges: draft.map(({ source, target, qty }) => ({ source, target, qty })) });
      toast.success(`Committed: ${r.data.updated} BOM(s) updated, ${r.data.created} created`); await load(); refreshAll(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const onNodeDragStop = (_, n) => { api.put("/bom-graph/layout", { positions: { [n.id]: n.position } }).catch(() => {}); };
  const rearrange = async () => { await api.delete("/bom-graph/layout"); setNodes([]); setG({ ...g, nodes: g.nodes.map((n) => ({ ...n, position: null })) }); toast.success("Layout reset"); };
  const onNodeClick = (_, n) => { if (pathMode && byId[n.id]?.tier === 3) setPathRoot(pathRoot === n.id ? null : n.id); };

  return (
    <div>
      <PageHeader kicker="// MATERIAL FLOW · T1 → T2 STAGES → T3" title="BOM Graph">
        <Toggle testId="graph-path-toggle" on={pathMode} onClick={() => { setPathMode(!pathMode); setPathRoot(null); }} icon={Route} label="Production Path" />
        <Toggle testId="graph-overlay-toggle" on={overlay} onClick={() => setOverlay(!overlay)} icon={Siren} label="Shortages" />
        <button data-testid="graph-rearrange-btn" onClick={rearrange} className="h-12 px-4 rounded-md border border-[#334155] text-sm font-semibold flex items-center gap-2"><LayoutGrid className="w-4 h-4" />Re-arrange</button>
        {dirty && <button data-testid="graph-discard-btn" onClick={() => setDraft(g.edges)} className="h-12 px-4 rounded-md border border-[#334155] text-sm font-semibold flex items-center gap-2"><Undo2 className="w-4 h-4" />Discard</button>}
        <button data-testid="graph-commit-btn" disabled={!dirty || !!chain || busy} onClick={commit} title={chain ? "Resolve the loop first" : ""} className="h-12 px-5 rounded-md bg-[#F59E0B] text-[#090D16] font-bold flex items-center gap-2 disabled:opacity-40"><Save className="w-4 h-4" />{busy ? "Committing…" : "Commit"}</button>
      </PageHeader>
      <div className="font-mono text-[11px] text-[#6B7280] mb-3 flex flex-wrap gap-4" data-testid="graph-help">
        <span><GitBranch className="inline w-3 h-3" /> Drag from a node's right handle to another node's left handle to add a component</span>
        <span>Click an arrow to edit or remove it</span>
        {pathMode && <span className="text-[#F59E0B]">Tap a T3 finished good to trace its production path</span>}
        {dirty && <span className="text-[#F59E0B]" data-testid="graph-dirty">UNCOMMITTED CHANGES</span>}
      </div>
      <div className="relative h-[calc(100vh-230px)] min-h-[480px] rounded-md border border-[#1E293B] overflow-hidden bg-[#090D16]" data-testid="bom-graph-canvas">
        <LoopAlert chain={chain} byId={byId} />
        {detailFor && <BomDetailCard productId={detailFor} onClose={() => setDetailFor(null)} />}
        {g && (
          <ReactFlow nodes={nodes} edges={rfEdges} nodeTypes={nodeTypes} onNodesChange={onNodesChange} onConnect={onConnect}
            onEdgeClick={(_, e) => { const d = draft.find((x) => x.id === e.id); if (d) setEdit({ ...d, isNew: false }); }}
            onNodeClick={onNodeClick} onNodeDragStop={onNodeDragStop} colorMode="dark" fitView minZoom={0.15} proOptions={{ hideAttribution: true }}>
            <Background color="#1E293B" gap={24} />
            <Controls />
            <MiniMap pannable zoomable maskColor="rgba(9,13,22,0.7)" nodeColor={(n) => (n.data.node.tier === 1 ? "#38BDF8" : n.data.node.tier === 2 ? "#A78BFA" : "#F59E0B")} />
          </ReactFlow>
        )}
      </div>
      <QtyDialog edit={edit} byId={byId} onSave={saveEdit} onDelete={removeEdit} onClose={() => setEdit(null)} />
      {createFor && <BomEditor mode="new" bom={null} presetProductId={createFor} onClose={() => { setCreateFor(null); load(); }} />}
    </div>
  );
}
